import React from "react";
import { AvailabilityData } from "../../../services/availabilityManagementApi";
import { ActionButton } from "../../shared";
import { Icons } from "../../shared/Icons";

interface AvailabilityProps {
  availability: AvailabilityData;
  professionalName?: string;
  setAvailability: React.Dispatch<React.SetStateAction<AvailabilityData>>;
  onSave: () => void;
}

export const Availability: React.FC<AvailabilityProps> = ({
  availability,
  professionalName,
  setAvailability,
  onSave,
}) => {
  const addInterval = (dayKey: keyof typeof availability) => {
    setAvailability({
      ...availability,
      [dayKey]: {
        ...availability[dayKey],
        intervals: [
          ...availability[dayKey].intervals,
          { start: "09:00", end: "17:00" },
        ],
      },
    });
  };

  const removeInterval = (dayKey: keyof typeof availability, index: number) => {
    setAvailability({
      ...availability,
      [dayKey]: {
        ...availability[dayKey],
        intervals: availability[dayKey].intervals.filter((_, i) => i !== index),
      },
    });
  };

  const updateInterval = (
    dayKey: keyof typeof availability,
    index: number,
    field: "start" | "end",
    value: string
  ) => {
    const newIntervals = [...availability[dayKey].intervals];
    newIntervals[index] = { ...newIntervals[index], [field]: value };
    setAvailability({
      ...availability,
      [dayKey]: {
        ...availability[dayKey],
        intervals: newIntervals,
      },
    });
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Icons.Schedule sx={{ fontSize: 28 }} />
          Horário de Funcionamento{" "}
          {professionalName ? `- ${professionalName}` : ""}
        </h3>
      </div>
      <div className="p-6 space-y-4">
        {[
          { key: "monday", label: "Segunda-feira" },
          { key: "tuesday", label: "Terça-feira" },
          { key: "wednesday", label: "Quarta-feira" },
          { key: "thursday", label: "Quinta-feira" },
          { key: "friday", label: "Sexta-feira" },
          { key: "saturday", label: "Sábado" },
          { key: "sunday", label: "Domingo" },
        ].map(({ key, label }) => {
          const dayKey = key as keyof typeof availability;
          return (
            <div
              key={key}
              className="border-b border-gray-100 dark:border-slate-700 pb-4 last:border-b-0"
            >
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">
                  {label}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={availability[dayKey].open}
                    onChange={(e) =>
                      setAvailability({
                        ...availability,
                        [dayKey]: {
                          ...availability[dayKey],
                          open: e.target.checked,
                        },
                      })
                    }
                    className="rounded border-gray-300 dark:border-slate-600 dark:bg-slate-900 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-600 dark:text-slate-300">Aberto</span>
                </label>
              </div>
              {availability[dayKey].open && (
                <div className="space-y-3">
                  {availability[dayKey].intervals.map((interval, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <input
                        type="time"
                        value={interval.start}
                        onChange={(e) =>
                          updateInterval(dayKey, index, "start", e.target.value)
                        }
                        className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 
                                 focus:border-emerald-500 transition-colors duration-200"
                      />
                      <span className="text-gray-500 dark:text-slate-400 font-medium">até</span>
                      <input
                        type="time"
                        value={interval.end}
                        onChange={(e) =>
                          updateInterval(dayKey, index, "end", e.target.value)
                        }
                        className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500 
                                 focus:border-emerald-500 transition-colors duration-200"
                      />
                      {availability[dayKey].intervals.length > 1 && (
                        <button
                          onClick={() => removeInterval(dayKey, index)}
                          className="p-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg 
                                   transition-colors duration-200 flex-shrink-0"
                          title="Remover horário"
                        >
                          <Icons.Delete sx={{ fontSize: 20 }} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => addInterval(dayKey)}
                    className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 
                             hover:bg-emerald-50 dark:hover:bg-emerald-950/40 px-3 py-2 rounded-lg transition-colors duration-200"
                  >
                    <Icons.Add sx={{ fontSize: 20 }} />
                    <span className="text-sm font-medium">
                      Adicionar horário
                    </span>
                  </button>
                </div>
              )}
              {!availability[dayKey].open && (
                <p className="text-sm text-gray-500 dark:text-slate-400 italic">Fechado</p>
              )}
            </div>
          );
        })}
        <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
          <ActionButton
            onClick={onSave}
            variant="primary"
            size="sm"
            className="w-full justify-center"
            icon={<Icons.Schedule sx={{ fontSize: 18 }} />}
          >
            Salvar Horários
          </ActionButton>
        </div>
      </div>
    </div>
  );
};
